import { toast } from '@teamsparta/stack-toast';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import type { GrassCampaignResponseDto } from '@/api/campaign/grass/grass.dto';
import {
  useCertificateInfo,
  useGithubCallback,
  useGithubData,
  useTotalContribution,
} from '@/hooks/react-query/campaign';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { useGrassDonationModal } from '@/store/campaign';
import { useLoginFailedModal } from '@/store/campaign/failed';

export const useCampaignLogic = () => {
  const router = useRouter();
  const { open, close } = useGrassDonationModal();
  const { open: loginFailedModalOpen } = useLoginFailedModal();
  const { mutate: handleGithubCallback } = useGithubCallback();
  const { data: grassData } = useGithubData();
  const { data: contributionData } = useTotalContribution();
  const { data: certificateData } = useCertificateInfo(
    router.query.id as string,
  );

  const modalData = router.query.id ? certificateData : grassData;
  const dynamicCampaignRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!router.query.id) {
      document.body.style.overflow = 'unset';
    }
  }, [router.query.id]);

  useEffect(() => {
    if (router.query.id) {
      const savedScrollPosition = sessionStorage.getItem('scrollPosition');
      if (savedScrollPosition) {
        const timeoutId = setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
        }, 0);
        return () => clearTimeout(timeoutId);
      }
      open();
      document.body.style.overflow = 'hidden';
      return;
    }
  }, [router.query.id, open, loginFailedModalOpen, grassData, close]);

  useEffect(() => {
    const code = router.query.code;

    if (code && typeof code === 'string') {
      handleGithubCallback(code, {
        onSuccess: (data) => {
          if (data) {
            setTimeout(() => {
              dynamicCampaignRef.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }, 100);
          }
        },
        onError: (error) => {
          console.error('GitHub OAuth Callback Error:', error);
          toast.error(
            'GitHub 로그인 중 오류가 발생했습니다. 다시 시도해주세요.',
            {
              position: 'bottom-center',
            },
          );
          loginFailedModalOpen();
        },
      });
    }
  }, [router.query.code, handleGithubCallback, loginFailedModalOpen]);

  return {
    modalData,
    grassData,
    contributionData,
    dynamicCampaignRef,
  };
};

export const formatNumber = (number?: number): string => {
  if (!number && number !== 0) {
    return '0';
  }
  return number.toLocaleString();
};

interface GitHubIdentificationResult {
  isShared: boolean;
  username: string | undefined;
}

interface StoredGithubUserData {
  data: {
    gitHubUserId: string;
    gitHubUsername: string;
  };
}

export const identifyGithubUser = (
  grassData: GrassCampaignResponseDto | null,
  urlParamId: string | string[] | undefined,
): GitHubIdentificationResult => {
  const storedData = localStorage.getItem('github-user-data');

  // URL 파라미터가 없는 경우 (참여 페이지)
  if (!urlParamId) {
    return {
      isShared: false,
      username: grassData?.gitHubUsername,
    };
  }

  // 로컬 스토리지에 데이터가 없는 경우 (공유받은 경우)
  if (!storedData) {
    return {
      isShared: true,
      username: grassData?.gitHubUsername,
    };
  }

  try {
    const localUserData: StoredGithubUserData = JSON.parse(storedData);
    const localId = localUserData.data.gitHubUserId;

    // 로컬 ID와 파라미터 ID가 다른 경우 (공유받은 경우)
    if (localId !== urlParamId) {
      return {
        isShared: true,
        username: grassData?.gitHubUsername,
      };
    }

    // 본인의 기부증인 경우
    return {
      isShared: false,
      username: localUserData.data.gitHubUsername,
    };
  } catch (error) {
    console.error('Error parsing stored data:', error);
    return {
      isShared: true,
      username: grassData?.gitHubUsername,
    };
  }
};

export const getGithubDisplayMessage = (
  grassData: GrassCampaignResponseDto | null | undefined,
  urlParamId: string | string[] | undefined,
): string => {
  if (!grassData || grassData.totalContributionsIn2024 === 0) {
    return '나의 깃허브에서 잔디를 불러와\n트리를 채우고 기부증을 받아보세요';
  }

  const { username } = identifyGithubUser(grassData, urlParamId);
  return `${username}님의\n 잔디 ${grassData.totalContributionsIn2024}개가 기부되어\n트리가 채워졌어요`;
};

export const copyCurrentUrl = () => {
  const currentUrl = window.location.href;

  copyToClipboard(currentUrl, {
    onSuccess: () => {
      toast.success('링크가 복사되었습니다.', {
        position: 'bottom-center',
      });
    },
  });
};
